import { CSSProperties, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

interface Modal {
    id?: string;
    title?: ReactNode;
    content: ReactNode | ((id: string) => ReactNode);
    show?: boolean;
    size?: number | string;
    onClose?: () => any;
    toFront?: boolean;
    createdAt?: Date;
    preventClose?: boolean;
    /**
     * Keeps the × but asks before closing, for unsaved work. `true` uses the generic
     * message, anything else is shown as the message.
     */
    confirmClose?: ReactNode;
    /** Extra classes on the modal shell, e.g. a max-width or another background */
    className?: string;
    /** Extra classes on the title bar, e.g. tw-mb-0 so the body can sit flush below it */
    headerClassName?: string;
    /** Extra classes on the scrollable body — tw-p-0 gives content the full width */
    bodyClassName?: string;
    /** Overrides on the body's inline style, e.g. dropping the reserved scrollbar gutter */
    bodyStyle?: CSSProperties;
}

export type ModalWithId = Modal & { id: string };

export interface ModalManagerStore {
    modals: ModalWithId[];
    addModal: (modal: Modal) => void;
    setModal: (modal: Modal) => void;
    removeModal: (id: string) => void;
    resetModals: () => void;
    /**
     * Closes everything a user could have closed anyway. Navigation uses this instead of
     * `resetModals`, so a `preventClose` modal — the form migration gate is one — cannot be
     * escaped by pressing the browser's back button. `confirmClose` modals stay too, since back
     * would otherwise throw away exactly what the confirm protects.
     */
    resetClosableModals: () => void;
    /**
     * Patches one open modal after it was added — chiefly `preventClose`, for
     * a form that only knows it is dirty once the club starts typing. Every
     * other field on `Modal` was already fixed at open time; this exists
     * because that one specifically is not.
     */
    updateModal: (id: string, patch: Partial<Modal>) => void;
}

const useModalManagerStore = create<ModalManagerStore>()((set) => ({
    modals: [],
    setModal: (modal) =>
        set(() => {
            const id = uuidv4();
            return {
                modals: [
                    {
                        ...modal,
                        content: typeof modal.content === 'function' ? modal.content(id) : modal.content,
                        id,
                        createdAt: new Date()
                    }
                ]
            };
        }),
    addModal: (modal) =>
        set((state) => {
            const id = uuidv4();
            return {
                modals: [
                    ...state.modals,
                    {
                        ...modal,
                        createdAt: new Date(),
                        content: typeof modal.content === 'function' ? modal.content(id) : modal.content,
                        id
                    }
                ]
            };
        }),
    removeModal: (id) => set((state) => ({ modals: state.modals.filter((modal) => modal.id !== id) })),
    resetModals: () => set({ modals: [] }),
    resetClosableModals: () => set((state) => ({ modals: state.modals.filter((modal) => modal.preventClose || modal.confirmClose) })),
    updateModal: (id, patch) =>
        set((state) => ({
            modals: state.modals.map((modal) => (modal.id === id ? { ...modal, ...patch } : modal))
        }))
}));

export default useModalManagerStore;
